/**
 * Audio trimming utilities using Web Audio API.
 * Downloads, decodes, slices, and re-encodes audio as WAV.
 */

/**
 * Trim audio from a URL to a specified time range and return a WAV Blob.
 */
export async function trimAudio(
  audioUrl: string,
  startTime: number,
  endTime: number
): Promise<Blob> {
  const response = await fetch(audioUrl);
  const arrayBuffer = await response.arrayBuffer();

  const audioContext = new AudioContext();
  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const trimmedBuffer = sliceAudioBuffer(audioContext, audioBuffer, startTime, endTime);
    return encodeWav(trimmedBuffer);
  } finally {
    await audioContext.close();
  }
}

/**
 * Trim audio from a Blob to a specified time range and return a WAV Blob.
 */
export async function trimAudioBlob(
  blob: Blob,
  startTime: number,
  endTime: number
): Promise<Blob> {
  const arrayBuffer = await blob.arrayBuffer();

  const audioContext = new AudioContext();
  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const trimmedBuffer = sliceAudioBuffer(audioContext, audioBuffer, startTime, endTime);
    return encodeWav(trimmedBuffer);
  } finally {
    await audioContext.close();
  }
}

/**
 * Save a Blob to the user's downloads folder under `filename`.
 *
 * Every audio download routes through a blob URL rather than pointing an
 * `<a download>` straight at the presigned S3 URL: the `download` attribute is
 * ignored for cross-origin hrefs, so linking at S3 directly would open the
 * clip in a tab (or save it under the opaque S3 key) instead of naming it.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on a delay — Safari needs the URL to outlive the synthetic click.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Fetch a URL and save the response body under `filename`.
 */
export async function downloadUrlAs(url: string, filename: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`);
  }
  saveBlob(await response.blob(), filename);
}

/**
 * Slice an AudioBuffer to a time range.
 */
function sliceAudioBuffer(
  ctx: AudioContext,
  buffer: AudioBuffer,
  startTime: number,
  endTime: number
): AudioBuffer {
  const sampleRate = buffer.sampleRate;
  const startSample = Math.floor(startTime * sampleRate);
  const endSample = Math.min(Math.floor(endTime * sampleRate), buffer.length);
  const length = endSample - startSample;

  if (length <= 0) {
    throw new Error("Invalid trim range: no samples in selection");
  }

  const trimmed = ctx.createBuffer(buffer.numberOfChannels, length, sampleRate);

  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const sourceData = buffer.getChannelData(channel);
    const targetData = trimmed.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      targetData[i] = sourceData[startSample + i];
    }
  }

  return trimmed;
}

/**
 * Encode an AudioBuffer as a WAV file Blob.
 * Writes a standard 44-byte WAV header + raw 16-bit PCM data.
 */
function encodeWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const numSamples = buffer.length;
  const dataSize = numSamples * blockAlign;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;

  const arrayBuffer = new ArrayBuffer(totalSize);
  const view = new DataView(arrayBuffer);

  // RIFF header
  writeString(view, 0, "RIFF");
  view.setUint32(4, totalSize - 8, true);
  writeString(view, 8, "WAVE");

  // fmt subchunk
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true); // subchunk1 size (PCM = 16)
  view.setUint16(20, 1, true); // audio format (PCM = 1)
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true); // byte rate
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);

  // data subchunk
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  // Interleave channels and write 16-bit PCM samples
  let offset = headerSize;
  for (let i = 0; i < numSamples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = buffer.getChannelData(ch)[i];
      // Clamp to [-1, 1] and convert to 16-bit integer
      const clamped = Math.max(-1, Math.min(1, sample));
      const int16 = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      view.setInt16(offset, int16, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: "audio/wav" });
}

function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}
